import { useEffect, useMemo, useState, useCallback } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useApplicantContext } from '../contexts/ApplicantContext';
import { Card, Button, Label, Input } from '../components/ui';
import { PageHeader } from '../components/PageHeader';
import { CATEGORY_STYLES } from '../components/milestoneColors';
import { Monogram } from '../components/Monogram';
import {
  deriveAllMilestonesWithSchools,
  groupPlannerByMonth,
  CATEGORY_LABELS,
  type PlannerMilestone,
  type MilestoneCategory,
} from '../lib/planner';
import {
  loadPlannerOffsets,
  savePlannerOffsets,
  type PlannerOffsetValues,
} from '../lib/plannerSettings';
import {
  reconcileMilestones,
  setMilestoneStatus,
  setMilestoneLinkedDocuments,
} from '../lib/plannerPersistence';
import type {
  Scholarship, TargetSchool, TimelineMilestone, DocumentRecord,
} from '@scholarship-app/types';
import {
  CalendarDays, List, SlidersHorizontal, ChevronDown, ChevronUp, AlertTriangle,
  Circle, CircleDot, CheckCircle2, X, ExternalLink, CalendarClock, FileText, Link2, Plus,
} from 'lucide-react';
import { parseISO } from 'date-fns';
import { safeFormatDate } from '../lib/dates';

// =============================================================================
// PlannerPage — Senior Year Planner (v0.2.35, Wave B1)
//
// Wave B1: milestones are now PERSISTED. On load the page derives the
// milestone set from scholarships, reconciles it against stored milestones
// (see lib/plannerPersistence.ts — creates new, updates moved dates, deletes
// untouched-and-stale, never clobbers human status), then renders from the
// stored set with a status control on each milestone.
// =============================================================================

const OFFSET_FIELDS: { key: keyof PlannerOffsetValues; label: string }[] = [
  { key: 'recommendation_request_days_before', label: 'Request recommendations' },
  { key: 'essay_draft_days_before', label: 'Draft essay' },
  { key: 'transcript_request_days_before', label: 'Request transcript' },
  { key: 'test_scores_send_days_before', label: 'Send test scores' },
  { key: 'portfolio_days_before', label: 'Prepare portfolio' },
  { key: 'essay_final_days_before', label: 'Finalize essay' },
];

const CATEGORIES: MilestoneCategory[] = [
  'essay', 'recommendation', 'transcript_scores', 'portfolio', 'submit', 'admission',
];

const todayIso = () => new Date().toISOString().slice(0, 10);

// Cycle order for the status control.
const NEXT_STATUS: Record<PlannerMilestone['status'], PlannerMilestone['status']> = {
  pending: 'in_progress',
  in_progress: 'complete',
  complete: 'pending',
};

function StatusButton({
  status, onCycle,
}: {
  status: PlannerMilestone['status'];
  onCycle: () => void;
}) {
  const icon =
    status === 'complete' ? <CheckCircle2 size={18} className="text-emerald-600" />
    : status === 'in_progress' ? <CircleDot size={18} className="text-amber-500" />
    : <Circle size={18} className="text-line" />;
  const label =
    status === 'complete' ? 'Complete'
    : status === 'in_progress' ? 'In progress' : 'Pending';
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onCycle(); }}
      title={`${label} — tap to change`}
      aria-label={`Status: ${label}. Tap to change.`}
      className="shrink-0"
    >
      {icon}
    </button>
  );
}

function MilestoneRow({
  m, dimmed, highlighted, onHover, onCycleStatus, onOpenDetail,
}: {
  m: PlannerMilestone;
  dimmed: boolean;
  highlighted: boolean;
  onHover: (scholarshipId: string | null) => void;
  onCycleStatus: (m: PlannerMilestone) => void;
  onOpenDetail: (m: PlannerMilestone) => void;
}) {
  const style = CATEGORY_STYLES[m.category];
  const overdue = !m.isComplete && m.targetDate < todayIso();
  return (
    <div
      onMouseEnter={() => onHover(m.scholarshipId)}
      onMouseLeave={() => onHover(null)}
      className={`flex items-center gap-2.5 rounded-md border border-line border-l-4 ${style.bar}
        px-3 py-2 transition-opacity ${dimmed ? 'opacity-35' : ''}
        ${highlighted ? 'ring-2 ring-forest-300' : ''}
        ${m.isComplete ? 'bg-cream' : ''}`}
    >
      <StatusButton status={m.status} onCycle={() => onCycleStatus(m)} />
      <Monogram name={m.scholarshipName} size={26} />
      <button
        type="button"
        onClick={() => onOpenDetail(m)}
        className="min-w-0 flex-1 text-left"
      >
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${style.dot}`} />
            <span className={`font-medium text-sm ${
              m.isComplete ? 'text-soft line-through' : 'text-ink'
            }`}>
              {m.title}
            </span>
            {overdue && (
              <span className="inline-flex items-center gap-0.5 text-xs text-rose-700">
                <AlertTriangle size={11} /> overdue
              </span>
            )}
          </div>
          <span className="text-xs text-soft">
            {safeFormatDate(m.targetDate, 'EEE, MMM d') ?? 'Date TBD'}
          </span>
        </div>
        <div className="mt-0.5 text-xs text-soft">
          {m.scholarshipName}
          {!m.isFinalSubmit && (
            <> · for the {safeFormatDate(m.deadlineDate, 'MMM d') ?? 'TBD'} deadline</>
          )}
        </div>
      </button>
    </div>
  );
}

// --- Milestone detail modal -------------------------------------------------

function MilestoneDetail({
  m, documents, linkedDocumentIds, onClose, onOpenScholarship, onSetLinkedDocuments,
}: {
  m: PlannerMilestone;
  documents: DocumentRecord[];
  linkedDocumentIds: string[];
  onClose: () => void;
  onOpenScholarship: (scholarshipId: string) => void;
  onSetLinkedDocuments: (milestoneId: string, documentIds: string[]) => void;
}) {
  const style = CATEGORY_STYLES[m.category];
  const overdue = !m.isComplete && m.targetDate < todayIso();
  const statusLabel =
    m.status === 'complete' ? 'Complete'
    : m.status === 'in_progress' ? 'In progress' : 'Pending';
  const isAdmission = m.sourceType === 'admission';

  const [picking, setPicking] = useState(false);

  const availableDocs = documents.filter((d) => !d.archived);
  const linkedDocs = availableDocs.filter((d) => linkedDocumentIds.includes(d.document_id));
  const unlinkedDocs = availableDocs.filter((d) => !linkedDocumentIds.includes(d.document_id));

  function link(docId: string) {
    onSetLinkedDocuments(m.id, [...linkedDocumentIds, docId]);
    setPicking(false);
  }
  function unlink(docId: string) {
    onSetLinkedDocuments(m.id, linkedDocumentIds.filter((id) => id !== docId));
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center bg-black/40 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="mt-16 w-full max-w-md rounded-lg bg-paper p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <Monogram name={m.scholarshipName} size={36} />
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-ink">{m.title}</h2>
              <p className="text-xs text-soft truncate">{m.scholarshipName}</p>
            </div>
          </div>
          <button type="button" onClick={onClose}
            className="text-soft hover:text-soft" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <dl className="mt-4 space-y-2 text-sm">
          <div className="flex justify-between gap-3">
            <dt className="text-soft">Category</dt>
            <dd className="flex items-center gap-1.5">
              <span className={`h-2.5 w-2.5 rounded-full ${style.dot}`} />
              {CATEGORY_LABELS[m.category]}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-soft">Target date</dt>
            <dd className={overdue ? 'text-rose-700' : 'text-ink'}>
              {safeFormatDate(m.targetDate, 'EEEE, MMMM d, yyyy') ?? 'Date TBD'}
              {overdue && ' (overdue)'}
            </dd>
          </div>
          {!m.isFinalSubmit && (
            <>
              <div className="flex justify-between gap-3">
                <dt className="text-soft">Deadline</dt>
                <dd className="text-ink">
                  {safeFormatDate(m.deadlineDate, 'MMMM d, yyyy') ?? 'TBD'}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-soft">Lead time</dt>
                <dd className="text-ink">
                  {m.daysBeforeDeadline} days before the deadline
                </dd>
              </div>
            </>
          )}
          <div className="flex justify-between gap-3">
            <dt className="text-soft">Status</dt>
            <dd className="text-ink">{statusLabel}</dd>
          </div>
        </dl>

        {/* Linked documents (v0.2.37) */}
        <div className="mt-4 border-t border-line pt-3">
          <h3 className="flex items-center gap-1.5 text-sm font-medium text-ink mb-1.5">
            <FileText size={14} /> Linked documents
          </h3>
          {linkedDocs.length === 0 && !picking && (
            <p className="text-xs text-soft mb-1.5">
              No documents linked. Link an essay or file from the Documents tab.
            </p>
          )}
          {linkedDocs.length > 0 && (
            <ul className="space-y-1 mb-1.5">
              {linkedDocs.map((d) => (
                <li key={d.document_id}
                  className="flex items-center justify-between gap-2 rounded-md border border-line px-2 py-1.5">
                  <span className="flex items-center gap-1.5 min-w-0 text-sm">
                    <Link2 size={12} className="text-soft shrink-0" />
                    <span className="truncate">{d.title}</span>
                  </span>
                  <button type="button" onClick={() => unlink(d.document_id)}
                    className="text-soft hover:text-rose-600 shrink-0" aria-label="Unlink">
                    <X size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {picking ? (
            <div className="rounded-md border border-line">
              {unlinkedDocs.length === 0 ? (
                <p className="px-2 py-2 text-xs text-soft">
                  No more documents to link. Add documents in the Documents tab.
                </p>
              ) : (
                <ul className="max-h-40 overflow-auto">
                  {unlinkedDocs.map((d) => (
                    <li key={d.document_id}>
                      <button type="button" onClick={() => link(d.document_id)}
                        className="flex w-full items-center gap-1.5 px-2 py-1.5 text-left text-sm hover:bg-forest-50">
                        <FileText size={12} className="text-soft shrink-0" />
                        <span className="truncate">{d.title}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <button type="button" onClick={() => setPicking(false)}
                className="w-full border-t border-line px-2 py-1 text-xs text-soft hover:text-ink">
                Cancel
              </button>
            </div>
          ) : (
            <button type="button" onClick={() => setPicking(true)}
              className="inline-flex items-center gap-1 text-xs text-forest-700 hover:underline">
              <Plus size={12} /> Link a document
            </button>
          )}
        </div>

        {/* Open the parent scholarship — only for scholarship milestones. */}
        {!isAdmission && m.scholarshipId && (
          <div className="mt-4 border-t border-line pt-3">
            <button
              type="button"
              onClick={() => onOpenScholarship(m.scholarshipId)}
              className="inline-flex items-center gap-1 text-sm text-forest-700 hover:underline"
            >
              <ExternalLink size={13} /> Open scholarship
            </button>
          </div>
        )}
        {isAdmission && (
          <div className="mt-4 border-t border-line pt-3 text-xs text-soft">
            This is a college application deadline from your target schools.
          </div>
        )}
      </div>
    </div>
  );
}

export function PlannerPage() {
  const { user, membership } = useAuth();
  const { selectedApplicantId } = useApplicantContext();
  const navigate = useNavigate();

  const [scholarships, setScholarships] = useState<Scholarship[]>([]);
  const [scholarshipsLoaded, setScholarshipsLoaded] = useState(false);
  const [targetSchools, setTargetSchools] = useState<TargetSchool[]>([]);
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [offsets, setOffsets] = useState<PlannerOffsetValues | null>(null);
  const [offsetDraft, setOffsetDraft] = useState<PlannerOffsetValues | null>(null);
  const [stored, setStored] = useState<TimelineMilestone[]>([]);
  const [reconciling, setReconciling] = useState(true);
  const [view, setView] = useState<'agenda' | 'calendar' | 'week'>('agenda');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savingOffsets, setSavingOffsets] = useState(false);
  const [hoverScholarship, setHoverScholarship] = useState<string | null>(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [calMonth, setCalMonth] = useState<string>(() => todayIso().slice(0, 7));
  const [selectedMilestone, setSelectedMilestone] = useState<PlannerMilestone | null>(null);

  // Scholarships (live).
  useEffect(() => {
    if (!db || !membership || !selectedApplicantId) return;
    const colRef = collection(
      db, 'households', membership.householdId, 'applicants', selectedApplicantId, 'scholarships'
    );
    const unsub = onSnapshot(colRef, (snap) => {
      setScholarships(snap.docs.map((d) => d.data() as Scholarship));
      setScholarshipsLoaded(true);
    });
    return () => unsub();
  }, [membership, selectedApplicantId]);

  // Target schools (live) — for admission-deadline milestones (v0.2.37).
  useEffect(() => {
    if (!db || !membership || !selectedApplicantId) return;
    const colRef = collection(
      db, 'households', membership.householdId, 'applicants', selectedApplicantId, 'target_schools'
    );
    const unsub = onSnapshot(colRef, (snap) => {
      setTargetSchools(snap.docs.map((d) => d.data() as TargetSchool));
    }, () => { /* non-fatal */ });
    return () => unsub();
  }, [membership, selectedApplicantId]);

  // Documents (live) — for linking documents to milestones (v0.2.37).
  useEffect(() => {
    if (!db || !membership || !selectedApplicantId) return;
    const colRef = collection(
      db, 'households', membership.householdId, 'applicants', selectedApplicantId, 'documents'
    );
    const unsub = onSnapshot(colRef, (snap) => {
      setDocuments(snap.docs.map((d) => d.data() as DocumentRecord));
    }, () => { /* non-fatal */ });
    return () => unsub();
  }, [membership, selectedApplicantId]);

  // Offset rules.
  useEffect(() => {
    if (!db || !membership) return;
    loadPlannerOffsets(db, membership.householdId).then((o) => {
      setOffsets(o);
      setOffsetDraft(o);
    });
  }, [membership]);

  // Reconcile: derive -> reconcile against stored -> hold the stored set.
  // Runs whenever the inputs (scholarships, offsets) change.
  const runReconcile = useCallback(async () => {
    if (!db || !membership || !selectedApplicantId || !user || !offsets) return;
    if (!scholarshipsLoaded) return;
    setReconciling(true);
    try {
      const derived = deriveAllMilestonesWithSchools(scholarships, targetSchools, offsets);
      const result = await reconcileMilestones(
        db, membership.householdId, selectedApplicantId, derived, user.uid
      );
      setStored(result);
    } catch (e) {
      console.error('[Planner] reconcile failed:', e);
    } finally {
      setReconciling(false);
    }
  }, [membership, selectedApplicantId, user, offsets, scholarships, targetSchools, scholarshipsLoaded]);

  useEffect(() => { runReconcile(); }, [runReconcile]);

  // Build the view-model: derived display fields + persisted status.
  const milestones: PlannerMilestone[] = useMemo(() => {
    if (!offsets) return [];
    const derived = deriveAllMilestonesWithSchools(scholarships, targetSchools, offsets);
    const derivedById = new Map(derived.map((d) => [d.id, d]));
    const out: PlannerMilestone[] = [];
    for (const s of stored) {
      const d = derivedById.get(s.milestone_id);
      const status = s.status === 'overdue' ? 'pending' : s.status;
      if (d) {
        out.push({ ...d, status, isComplete: status === 'complete' });
      } else {
        // Stored but no longer derived (kept because it had human status).
        // Reconstruct a minimal view-model from the stored record.
        const isAdmission = s.derived_from?.type === 'admission';
        out.push({
          id: s.milestone_id,
          sourceType: isAdmission ? 'admission' : 'scholarship',
          scholarshipId:
            (isAdmission ? s.derived_from?.school_id : s.derived_from?.scholarship_id) ?? '',
          scholarshipName: isAdmission ? '(school removed)' : '(scholarship removed)',
          category: categoryForType(s.milestone_type),
          title: s.title,
          targetDate: s.target_date,
          deadlineDate: s.derived_from?.anchor_date ?? s.target_date,
          daysBeforeDeadline: s.derived_from?.offset_days ?? 0,
          isOverdue: s.target_date < todayIso(),
          isFinalSubmit:
            s.milestone_type === 'scholarship_submit_by' ||
            s.milestone_type === 'application_submit_by',
          status,
          isComplete: status === 'complete',
        });
      }
    }
    out.sort((a, b) => a.targetDate.localeCompare(b.targetDate));
    return out;
  }, [stored, scholarships, targetSchools, offsets]);

  const visibleMilestones = useMemo(
    () => (showCompleted ? milestones : milestones.filter((m) => !m.isComplete)),
    [milestones, showCompleted]
  );
  const completedCount = milestones.filter((m) => m.isComplete).length;
  const months = useMemo(() => groupPlannerByMonth(visibleMilestones), [visibleMilestones]);

  // "This week" — milestones from today through the next 7 days (v0.2.37).
  const weekMilestones = useMemo(() => {
    const today = todayIso();
    const in7 = (() => {
      const d = new Date(today + 'T00:00:00');
      d.setDate(d.getDate() + 7);
      return d.toISOString().slice(0, 10);
    })();
    return visibleMilestones.filter(
      (m) => m.targetDate >= today && m.targetDate <= in7
    );
  }, [visibleMilestones]);

  // Blank-requirements nudge (v0.2.37): active scholarships that have a
  // deadline but no requirements set — so they only produce a Submit
  // milestone. Hint that requirements are missing.
  const blankRequirementScholarships = useMemo(() => {
    return scholarships.filter((s) => {
      if (s.archived) return false;
      if (s.status === 'submitted' || s.status === 'awarded' ||
          s.status === 'rejected' || s.status === 'expired') return false;
      if (!s.effective_deadline && !s.deadline) return false;
      const r = s.requirements;
      if (!r) return true;
      const hasAny =
        r.essay_required || r.letters_of_rec_required > 0 ||
        r.transcript_required || r.test_scores_required || r.portfolio_required;
      return !hasAny;
    });
  }, [scholarships]);

  async function handleCycleStatus(m: PlannerMilestone) {
    if (!db || !membership || !selectedApplicantId) return;
    const next = NEXT_STATUS[m.status];
    // Optimistic update.
    setStored((prev) =>
      prev.map((s) =>
        s.milestone_id === m.id
          ? { ...s, status: next, completed_at: next === 'complete' ? new Date().toISOString() : null }
          : s
      )
    );
    try {
      await setMilestoneStatus(
        db, membership.householdId, selectedApplicantId, m.id, next
      );
    } catch (e) {
      alert('Could not update status: ' + (e instanceof Error ? e.message : String(e)));
      runReconcile(); // resync on failure
    }
  }

  async function handleSetLinkedDocuments(milestoneId: string, documentIds: string[]) {
    if (!db || !membership || !selectedApplicantId) return;
    // Optimistic update of the stored set.
    setStored((prev) =>
      prev.map((s) =>
        s.milestone_id === milestoneId
          ? {
              ...s,
              linked_resources: {
                ...s.linked_resources,
                document_ids: documentIds,
              },
            }
          : s
      )
    );
    try {
      await setMilestoneLinkedDocuments(
        db, membership.householdId, selectedApplicantId, milestoneId, documentIds
      );
    } catch (e) {
      alert('Could not update linked documents: ' + (e instanceof Error ? e.message : String(e)));
      runReconcile();
    }
  }

  async function handleSaveOffsets() {
    if (!db || !membership || !user || !offsetDraft) return;
    setSavingOffsets(true);
    try {
      await savePlannerOffsets(db, membership.householdId, user.uid, offsetDraft);
      setOffsets(offsetDraft);
    } catch (e) {
      alert('Could not save settings: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSavingOffsets(false);
    }
  }

  if (!scholarshipsLoaded || !offsets || !offsetDraft) {
    return (
      <div className="space-y-4">
        <PageHeader title="Planner" description="Your senior-year application timeline." />
        <div className="text-soft">Loading planner…</div>
      </div>
    );
  }

  const offsetsDirty = JSON.stringify(offsets) !== JSON.stringify(offsetDraft);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Planner"
        description="Prep milestones, worked back from each scholarship's deadline."
      />

      {/* Settings card */}
      <Card className="p-0 overflow-hidden">
        <button
          type="button"
          onClick={() => setSettingsOpen((o) => !o)}
          className="flex w-full items-center justify-between px-4 py-3 text-left"
        >
          <span className="flex items-center gap-2 text-sm font-medium text-ink">
            <SlidersHorizontal size={15} /> Timeline settings
          </span>
          {settingsOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {settingsOpen && (
          <div className="border-t border-line px-4 py-3 space-y-3">
            <p className="text-xs text-soft">
              How many days before a scholarship's deadline each task should start.
              Changing these re-paces the timeline; milestones you've already
              marked done or in progress keep their status.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {OFFSET_FIELDS.map((f) => (
                <div key={f.key} className="flex items-center justify-between gap-2">
                  <Label>{f.label}</Label>
                  <div className="flex items-center gap-1.5">
                    <Input
                      type="number"
                      min={0}
                      value={offsetDraft[f.key]}
                      onChange={(e) =>
                        setOffsetDraft({
                          ...offsetDraft,
                          [f.key]: Math.max(0, Number(e.target.value) || 0),
                        })
                      }
                      className="w-20"
                    />
                    <span className="text-xs text-soft whitespace-nowrap">days before</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={handleSaveOffsets} disabled={!offsetsDirty || savingOffsets}>
                {savingOffsets ? 'Saving…' : 'Save settings'}
              </Button>
              {offsetsDirty && (
                <button
                  type="button"
                  onClick={() => setOffsetDraft(offsets)}
                  className="text-xs text-soft hover:text-ink"
                >
                  Discard changes
                </button>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Legend + controls */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <span key={c} className="inline-flex items-center gap-1 text-xs text-soft">
              <span className={`h-2.5 w-2.5 rounded-full ${CATEGORY_STYLES[c].dot}`} />
              {CATEGORY_LABELS[c]}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {completedCount > 0 && (
            <button
              type="button"
              onClick={() => setShowCompleted((v) => !v)}
              className="text-xs text-soft hover:text-ink"
            >
              {showCompleted ? 'Hide' : 'Show'} completed ({completedCount})
            </button>
          )}
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => setView('week')}
              className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-xs border ${
                view === 'week'
                  ? 'border-forest-300 bg-forest-50 text-forest-800'
                  : 'border-line text-soft'
              }`}
            >
              <CalendarClock size={13} /> This week
            </button>
            <button
              type="button"
              onClick={() => setView('agenda')}
              className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-xs border ${
                view === 'agenda'
                  ? 'border-forest-300 bg-forest-50 text-forest-800'
                  : 'border-line text-soft'
              }`}
            >
              <List size={13} /> Agenda
            </button>
            <button
              type="button"
              onClick={() => setView('calendar')}
              className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-xs border ${
                view === 'calendar'
                  ? 'border-forest-300 bg-forest-50 text-forest-800'
                  : 'border-line text-soft'
              }`}
            >
              <CalendarDays size={13} /> Calendar
            </button>
          </div>
        </div>
      </div>

      {/* Blank-requirements nudge (v0.2.37) */}
      {blankRequirementScholarships.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <span className="font-medium">
            {blankRequirementScholarships.length} scholarship
            {blankRequirementScholarships.length === 1 ? '' : 's'} with a deadline
            {blankRequirementScholarships.length === 1 ? ' has' : ' have'} no
            requirements set
          </span>{' '}
          — only a "Submit application" milestone shows for{' '}
          {blankRequirementScholarships.length === 1 ? 'it' : 'them'}. Set each
          one's requirements (essay, recommendations, etc.) on its detail page
          and prep milestones will appear.
        </div>
      )}

      {/* Body */}
      {milestones.length === 0 ? (
        <Card className="p-6 text-center text-soft text-sm">
          {reconciling
            ? 'Building your timeline…'
            : `No milestones yet. The planner builds a timeline from your
               scholarships' deadlines and your target schools' admission
               deadlines — add those and milestones will appear here.`}
        </Card>
      ) : view === 'week' ? (
        weekMilestones.length === 0 ? (
          <Card className="p-6 text-center text-soft text-sm">
            Nothing due in the next 7 days.
          </Card>
        ) : (
          <div className="space-y-1.5">
            <h3 className="text-sm font-semibold text-soft">
              Next 7 days
            </h3>
            {weekMilestones.map((m) => (
              <MilestoneRow
                key={m.id}
                m={m}
                dimmed={false}
                highlighted={false}
                onHover={() => {}}
                onCycleStatus={handleCycleStatus}
                onOpenDetail={setSelectedMilestone}
              />
            ))}
          </div>
        )
      ) : visibleMilestones.length === 0 ? (
        <Card className="p-6 text-center text-soft text-sm">
          All milestones complete. Nice work.
        </Card>
      ) : view === 'agenda' ? (
        <AgendaView
          months={months}
          hoverScholarship={hoverScholarship}
          onHover={setHoverScholarship}
          onCycleStatus={handleCycleStatus}
          onOpenDetail={setSelectedMilestone}
        />
      ) : (
        <CalendarView
          milestones={visibleMilestones}
          month={calMonth}
          onMonthChange={setCalMonth}
          onOpenDetail={setSelectedMilestone}
        />
      )}

      {selectedMilestone && (
        <MilestoneDetail
          m={selectedMilestone}
          documents={documents}
          linkedDocumentIds={
            stored.find((s) => s.milestone_id === selectedMilestone.id)
              ?.linked_resources?.document_ids ?? []
          }
          onClose={() => setSelectedMilestone(null)}
          onOpenScholarship={(sid) => {
            setSelectedMilestone(null);
            navigate(`/scholarships/${sid}`);
          }}
          onSetLinkedDocuments={handleSetLinkedDocuments}
        />
      )}
    </div>
  );
}

// --- helpers ----------------------------------------------------------------

function categoryForType(t: TimelineMilestone['milestone_type']): MilestoneCategory {
  switch (t) {
    case 'essay_draft_due':
    case 'essay_final_due':
      return 'essay';
    case 'recommendation_request_by':
    case 'recommendation_due':
      return 'recommendation';
    case 'transcript_request_by':
    case 'test_score_send_by':
      return 'transcript_scores';
    case 'portfolio_due':
      return 'portfolio';
    case 'application_submit_by':
      return 'admission';
    default:
      return 'submit';
  }
}

// --- Agenda view ------------------------------------------------------------

function AgendaView({
  months, hoverScholarship, onHover, onCycleStatus, onOpenDetail,
}: {
  months: { month: string; items: PlannerMilestone[] }[];
  hoverScholarship: string | null;
  onHover: (id: string | null) => void;
  onCycleStatus: (m: PlannerMilestone) => void;
  onOpenDetail: (m: PlannerMilestone) => void;
}) {
  return (
    <div className="space-y-4">
      {months.map(({ month, items }) => (
        <div key={month}>
          <h3 className="mb-1.5 text-sm font-semibold text-soft">
            {safeFormatDate(month + '-01', 'MMMM yyyy') ?? month}
          </h3>
          <div className="space-y-1.5">
            {items.map((m) => (
              <MilestoneRow
                key={m.id}
                m={m}
                dimmed={hoverScholarship != null && hoverScholarship !== m.scholarshipId}
                highlighted={hoverScholarship === m.scholarshipId}
                onHover={onHover}
                onCycleStatus={onCycleStatus}
                onOpenDetail={onOpenDetail}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// --- Calendar view ----------------------------------------------------------

function CalendarView({
  milestones, month, onMonthChange, onOpenDetail,
}: {
  milestones: PlannerMilestone[];
  month: string;
  onMonthChange: (m: string) => void;
  onOpenDetail: (m: PlannerMilestone) => void;
}) {
  const first = parseISO(month + '-01');
  const year = first.getFullYear();
  const monthIdx = first.getMonth();
  const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
  const startWeekday = first.getDay();

  const byDay = new Map<number, PlannerMilestone[]>();
  for (const m of milestones) {
    if (m.targetDate.slice(0, 7) !== month) continue;
    const day = Number(m.targetDate.slice(8, 10));
    if (!byDay.has(day)) byDay.set(day, []);
    byDay.get(day)!.push(m);
  }

  function shiftMonth(delta: number) {
    const d = new Date(year, monthIdx + delta, 1);
    onMonthChange(d.toISOString().slice(0, 7));
  }

  const cells: (number | null)[] = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <Card className="p-3">
      <div className="flex items-center justify-between mb-2">
        <button type="button" onClick={() => shiftMonth(-1)}
          className="text-sm text-soft hover:text-ink px-2">‹ Prev</button>
        <span className="text-sm font-semibold text-soft">
          {safeFormatDate(month + '-01', 'MMMM yyyy') ?? month}
        </span>
        <button type="button" onClick={() => shiftMonth(1)}
          className="text-sm text-soft hover:text-ink px-2">Next ›</button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-xs text-soft mb-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, i) => {
          const items = day != null ? byDay.get(day) ?? [] : [];
          return (
            <div
              key={i}
              className={`min-h-[64px] rounded-md border p-1 ${
                day == null ? 'border-transparent' : 'border-line'
              }`}
            >
              {day != null && (
                <>
                  <div className="text-xs text-soft">{day}</div>
                  <div className="mt-0.5 space-y-0.5">
                    {items.map((m) => (
                      <button
                        type="button"
                        key={m.id}
                        onClick={() => onOpenDetail(m)}
                        title={`${m.title} — ${m.scholarshipName}`}
                        className={`block w-full truncate rounded px-1 py-0.5 text-left text-[10px] ${
                          CATEGORY_STYLES[m.category].chip
                        } ${m.isComplete ? 'opacity-50 line-through' : ''}`}
                      >
                        {m.title}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
